import { useSyncExternalStore } from "react";
import { DEFAULT_PROFILE_ID } from "../settings/profileStore.js";

/**
 * The searches a profile has made, newest first, so a viewer can start typing and pick the term
 * instead of typing it all out (owner, 2026-10-13: "save and cache recent searches, maybe last
 * ten or so like other apps").
 *
 * Per profile, like favourites and history: a Kids profile never sees what an adult searched
 * for. The first profile keeps the key the global list used, so nothing already saved is lost.
 * Kept on the device only; it is a convenience, so a storage failure is silently no history.
 */

export const MAX_RECENT = 10;
const BASE_KEY = "slique-recent-searches";
/** Shorter than this is not a search worth keeping (the page itself searches from two). */
const MIN_LENGTH = 2;
const MAX_LENGTH = 80;

export function recentKey(profileId: string | undefined): string {
  return !profileId || profileId === DEFAULT_PROFILE_ID ? BASE_KEY : `${BASE_KEY}:${profileId}`;
}

const cache = new Map<string, string[]>();
const listeners = new Set<() => void>();
const EMPTY: string[] = [];

function load(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return EMPTY;
    return parsed.filter((term): term is string => typeof term === "string" && term.trim().length > 0).slice(0, MAX_RECENT);
  } catch {
    return EMPTY;
  }
}

/** The profile's recent searches, newest first. A stable array until something changes. */
export function readRecent(profileId: string | undefined): string[] {
  const key = recentKey(profileId);
  let held = cache.get(key);
  if (!held) {
    held = load(key);
    cache.set(key, held);
  }
  return held;
}

/** Remembers a search: trimmed, no duplicates (ignoring case), newest first, at most ten. */
export function addRecent(profileId: string | undefined, term: string): string[] {
  const trimmed = term.trim().replace(/\s+/g, " ").slice(0, MAX_LENGTH);
  const held = readRecent(profileId);
  if (trimmed.length < MIN_LENGTH) return held;
  const lowered = trimmed.toLowerCase();
  const next = [trimmed, ...held.filter((existing) => existing.toLowerCase() !== lowered)].slice(0, MAX_RECENT);
  const key = recentKey(profileId);
  cache.set(key, next);
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* private mode: recents are a convenience, never a hard failure */
  }
  for (const listener of [...listeners]) listener();
  return next;
}

/** Forgets what this module holds in memory. Tests only. */
export function resetRecentSearchesForTests(): void {
  cache.clear();
  for (const listener of [...listeners]) listener();
}

/**
 * The recent searches that go with what is typed: terms that START with it first, then terms that
 * contain it, each newest first. A term equal to what is typed is left out (picking it would do
 * nothing). Nothing typed gives them all.
 */
export function suggestionsFor(recent: readonly string[], typed: string, limit = MAX_RECENT): string[] {
  const needle = typed.trim().toLowerCase();
  if (!needle) return recent.slice(0, limit);
  const starts: string[] = [];
  const contains: string[] = [];
  for (const term of recent) {
    const lowered = term.toLowerCase();
    if (lowered === needle) continue;
    if (lowered.startsWith(needle)) starts.push(term);
    else if (lowered.includes(needle)) contains.push(term);
  }
  return [...starts, ...contains].slice(0, limit);
}

/** The profile's recent searches; switching profile answers for the other profile at once. */
export function useRecentSearches(profileId: string | undefined): string[] {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    () => readRecent(profileId),
    () => EMPTY,
  );
}
